import { forwardRef, useCallback, useLayoutEffect, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { PopoverAlign } from "@/lib/popoverAlign";

interface Props {
  anchorRef: RefObject<HTMLElement>;
  width: number;
  align?: PopoverAlign;
  testId?: string;
  children: ReactNode;
}

const GAP = 4;
const EDGE_PAD = 8;

const AnchoredPopover = forwardRef<HTMLDivElement, Props>(function AnchoredPopover(
  { anchorRef, width, align = "start", testId, children },
  ref,
) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [popover, setPopover] = useState<HTMLDivElement | null>(null);
  const attachRef = useCallback(
    (el: HTMLDivElement | null) => {
      setPopover(el);
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  useLayoutEffect(() => {
    function place() {
      const anchor = anchorRef.current;
      if (!anchor || !popover) return;
      const a = anchor.getBoundingClientRect();
      const height = popover.getBoundingClientRect().height;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = align === "end" ? a.right - width : a.left;
      left = Math.min(Math.max(EDGE_PAD, left), vw - width - EDGE_PAD);
      let top = a.bottom + GAP;
      if (top + height > vh - EDGE_PAD && a.top - GAP - height >= EDGE_PAD) top = a.top - GAP - height;
      setPos({ left, top });
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef, popover, width, align]);

  return createPortal(
    <div
      ref={attachRef}
      className="details-popover"
      style={{
        position: "fixed",
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        right: "auto",
        marginTop: 0,
        width,
        zIndex: 60,
        visibility: pos ? "visible" : "hidden",
      }}
      data-testid={testId}
    >
      {children}
    </div>,
    document.body,
  );
});

export default AnchoredPopover;
